import { useEffect, useState } from 'react';
import { api, ApiFailure, type PortalViewData } from '../api';
import { Insights, QualityPanel, Scorecards } from './Panels';

/**
 * The page an agency's client sees behind a portal link.
 *
 * The link is `/portal/{slug}#key={token}` — the fragment never leaves the
 * browser, so the token cannot leak into server logs. This page reads it and
 * POSTs it to the API. Like the share view: no sidebar, no session, no way to
 * reach the workspace behind the reports.
 *
 * Invalid, revoked and expired links all land on the same generic page — the
 * server deliberately does not say which one it was.
 */
export function PortalView({ slug }: { slug: string }) {
  const [data, setData] = useState<PortalViewData | null>(null);
  const [failure, setFailure] = useState<ApiFailure | null>(null);
  const [busy, setBusy] = useState(true);

  useEffect(() => {
    const token = /(?:^#|[#&])key=([^&]+)/.exec(window.location.hash)?.[1];

    if (token === undefined) {
      setFailure(
        new ApiFailure(
          'This portal link is not available.',
          'Use the full link your agency sent you, or ask them for a new one.',
          404,
        ),
      );
      setBusy(false);
      return;
    }

    api
      .viewPortal(slug, token)
      .then(setData)
      .catch((error) =>
        setFailure(
          error instanceof ApiFailure
            ? error
            : new ApiFailure('Could not open this portal.', 'Try again.', 0),
        ),
      )
      .finally(() => setBusy(false));
  }, [slug]);

  if (failure !== null) {
    return (
      <div className="shared-shell">
        <div className="card empty" style={{ maxWidth: 460 }}>
          <h3>{failure.message}</h3>
          <p>{failure.remedy}</p>
        </div>
      </div>
    );
  }

  if (busy || data === null) {
    return (
      <div className="shared-shell">
        <p className="muted">
          <span className="spinner" /> Opening portal…
        </p>
      </div>
    );
  }

  const { portal, branding, client, reports } = data;

  // Portal branding wins; the workspace's white-label settings fill the gaps.
  const companyName = portal.branding.companyName || branding?.companyName || null;
  const logoUrl = portal.branding.logoUrl ?? branding?.logoUrl ?? null;
  const accent = portal.branding.accentColor || branding?.accentColor || undefined;

  return (
    <div className="shared-page">
      <header className="shared-header" style={accent === undefined ? undefined : { borderTopColor: accent }}>
        <div className="page" style={{ paddingBottom: 0 }}>
          {logoUrl !== null && logoUrl !== '' && <img src={logoUrl} alt="" className="shared-logo" />}
          {companyName !== null && (
            <div className="eyebrow" style={accent === undefined ? undefined : { color: accent }}>
              {companyName.toUpperCase()}
            </div>
          )}
          <h1>{portal.name}</h1>
          <p className="muted">
            Prepared for {client.name} · read-only · {reports.length}{' '}
            {reports.length === 1 ? 'report' : 'reports'}
          </p>
        </div>
      </header>

      <div className="page">
        {reports.length === 0 && (
          <div className="card empty">
            <h3>No reports published yet</h3>
            <p>Your agency has not added any reports to this portal. Check back soon.</p>
          </div>
        )}

        {reports.map((report) => (
          <section key={report.id} style={{ marginBottom: 24 }}>
            <header style={{ marginBottom: 12 }}>
              <h2 style={{ margin: 0 }}>{report.title}</h2>
              <p className="muted" style={{ margin: '4px 0 0' }}>
                Report period ending {new Date(report.createdAt).toLocaleDateString()}
              </p>
            </header>

            <Scorecards cards={report.scorecards} />

            {report.narrative !== null && (
              <section className="card narrative">
                <header>
                  <h2>Summary</h2>
                </header>
                {report.narrative.summary.split('\n\n').map((paragraph, index) => (
                  <p key={index}>{paragraph.replace(/\*\*(.+?)\*\*/g, '$1')}</p>
                ))}
              </section>
            )}

            <section className="grid">
              <Insights insights={report.insights} />
              <QualityPanel quality={report.quality} />
            </section>
          </section>
        ))}

        <footer className="muted" style={{ textAlign: 'center', marginTop: 8 }}>
          {portal.branding.footerText !== null && <p>{portal.branding.footerText}</p>}
          {portal.branding.showDataVantageBranding && (
            <p>
              Made with{' '}
              <a href="/" style={{ color: 'var(--series-1)' }}>
                DataVantage
              </a>{' '}
              — every figure computed from the source data.
            </p>
          )}
        </footer>
      </div>
    </div>
  );
}
